// EditImageToolUI — branded tool-UI for the agent's `editImage` (Aidea Wizard) tool
// call, rendered inline in the Aidea Assistant chat thread.
//
// The tool result is `{ feature:'editImage', prompt?, settings:{ operation, ... },
// openPopup:true }`. Aidea Wizard edits the user's CURRENTLY SELECTED scene image, so
// unlike the other popups it is NOT opened through EditorModals (which doesn't
// render the animator). Instead we fire a dedicated `aidea:openImageAnimator`
// window event that the selected box's toolbar (EditorShortcuts) listens for and
// opens ImageAnimatorModal on that image, pre-set to the chosen operation +
// settings. We fire once (useRef guard) and re-fire at 0/500/1100ms to beat any
// listener/selection timing. The popup owns the actual edit + add-to-scene.
//
// Registration: makeAssistantToolUI({ toolName: "editImage", render }).

import React, { useCallback, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, ImageGenIcon, AlertIcon } from "../icons";
import { OpenPopupButton } from "./OpenPopupButton";
import { shouldAutoOpenOnce } from "../aiPopupGateway";
import { useChatCommit } from "../ChatCommitContext";
import {
  getInlineEditSession,
  isInlineEditActive,
  setInlineWorkingUrl,
  clearInlineEditSession,
} from "../inlineEditSession";
import { runImageEditJob, isInlineSupported } from "../runImageEditJob";
import { approveJob } from "../../../../common/server_utils.es6";
import { onJobsSettled, SETTLED_APPROVED } from "../../../utils/jobSettled";
import { mapAiProviderErrorMessage } from "../../../../common/ai_provider_errors.es6";

export const IMAGE_ANIMATOR_EVENT = "aidea:openImageAnimator";

// operation id -> readable label for the confirmation copy.
const OPERATION_LABELS = {
  animate: "Animate",
  "remove-background": "Remove Background",
  "remove-object": "Remove Object",
  "remove-text": "Remove Text",
  translate: "Translate Text",
  "generate-similar": "Generate Similar",
  "generate-image-from-image": "Edit Image",
  "change-image-text": "Change Text",
  resize: "Resize",
  upscale: "Upscale",
  "generate-from-reference": "Generate Similar Infographic",
  "replace-product": "Replace Object",
};

// Fire the dedicated open event, re-firing to beat the selected-box toolbar's
// listener/selection timing. Returns a cleanup that cancels pending re-fires.
function dispatchOpenImageAnimator(detail) {
  if (typeof window === "undefined") return () => {};
  const fire = () => window.dispatchEvent(new CustomEvent(IMAGE_ANIMATOR_EVENT, { detail }));
  fire();
  const t1 = setTimeout(fire, 500);
  const t2 = setTimeout(fire, 1100);
  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
  };
}

// Parse the tool result into { prompt, settings, openPopup } | { error } | null.
function parseEditImageResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  const openPopup = data.openPopup === true;
  const prompt = typeof data.prompt === "string" ? data.prompt.trim() : "";
  const settings = data.settings && typeof data.settings === "object" ? data.settings : {};
  // Optional reference to the image the agent is editing ({ boxId, navId,
  // imageUrl }) — lets Aidea Wizard open on that image even when it isn't the current
  // selection (e.g. on a restored conversation). Comes from the selected-element
  // context the agent received.
  const target = data.target && typeof data.target === "object" ? data.target : null;
  if (!openPopup && !settings.operation) return null;
  return { prompt, settings, openPopup, target };
}

function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <ImageGenIcon size={12} />
      {label}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

function RunningState() {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Preparing the image editor…
        </span>
      </div>
    </Card>
  );
}

function ErrorState({ message }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Edit Image" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">
          {mapAiProviderErrorMessage(message) || message || "Couldn't open the image editor."}
        </span>
      </div>
    </Card>
  );
}

// Fires the dedicated open event once (useRef guard) then shows a short branded
// confirmation. The Aidea Wizard editor opens on the user's SELECTED image.
function OpenedState({ prompt, settings, target, toolCallId }) {
  const dispatchedRef = useRef(false);

  const doOpen = useCallback(
    () =>
      dispatchOpenImageAnimator({
        operation: settings.operation,
        settings,
        prompt,
        // Pass the target image reference so EditorShortcuts can select + open
        // Aidea Wizard on it even when it isn't the current canvas selection.
        boxId: target?.boxId,
        navId: target?.navId,
        imageUrl: target?.imageUrl,
      }),
    [prompt, settings, target]
  );

  // Auto-open ONCE per tool call. Closing/reopening the chat (or restoring a
  // conversation) remounts this card; without the cross-mount guard every past
  // editImage card would re-open Aidea Wizard. The "Open" button below still opens on
  // demand.
  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    const key = toolCallId || `editImage:${settings.operation || ""}:${prompt || ""}`;
    if (!shouldAutoOpenOnce(key)) return undefined;
    const cancel = doOpen();
    return cancel;
  }, [doOpen, toolCallId, settings.operation, prompt]);

  const opLabel = OPERATION_LABELS[settings.operation] || "Edit";

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Edit Image" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          <ImageGenIcon size={16} />
        </span>
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
            {"Opened the image editor — "}
            <span className="tw-font-medium">{opLabel}</span>
            {" — on your selected image. Review and Generate."}
          </p>
          <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
            If nothing opened, select an image in your scene first, then ask again.
          </p>
          <OpenPopupButton label={`Open ${opLabel}`} onClick={doOpen} />
        </div>
      </div>
    </Card>
  );
}

// Inline-edit card: runs the requested edit on the session's WORKING image
// (started from the Image Animator top bar), previews before/after inline, and
// only swaps the canvas image when the user clicks "Uygula". Each result also
// advances the session's working URL so the next chat edit chains on it.
function InlineEditState({ prompt, settings, toolCallId }) {
  const { updateBoxImageUrl } = useChatCommit();
  const operation = settings.operation || "generate-image-from-image";
  // phase: running | done | applied | trayDeclined | error | fallback
  const [ui, setUi] = useState({ phase: "running", beforeUrl: null, afterUrl: null, error: null });
  const ranRef = useRef(false);
  // Bu kartın sonucunu üreten iş. İş tepsisinden onaylanırsa (tepsi sonucu
  // kutuya zaten uyguladı) ya da reddedilirse "Uygula" bayatlar.
  const jobIdRef = useRef(null);
  useEffect(
    () =>
      onJobsSettled((jobIds, outcomes) => {
        const id = jobIdRef.current;
        if (!id || !jobIds.includes(id)) return;
        setUi((s) =>
          s.phase !== "done"
            ? s
            : { ...s, phase: outcomes[id] === SETTLED_APPROVED ? "applied" : "trayDeclined" }
        );
      }),
    []
  );
  const opLabel = OPERATION_LABELS[operation] || "Edit";

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    // Run ONCE per tool call — remount/restore must not re-fire the job.
    if (!shouldAutoOpenOnce(`inline:${toolCallId || `${operation}:${prompt}`}`)) {
      setUi({ phase: "done", beforeUrl: null, afterUrl: null, error: null });
      return undefined;
    }
    const session = getInlineEditSession();
    if (!session) {
      setUi({
        phase: "error",
        error: "Düzenleme oturumu bulunamadı — üst bardaki butonla yeniden başlat.",
      });
      return undefined;
    }
    // Ops that need extra UI (region/reference/text-change/animate) can't run
    // inline — render OpenedState, which opens the existing popup itself.
    if (!isInlineSupported(operation)) {
      setUi({ phase: "fallback" });
      return undefined;
    }
    const beforeUrl = session.workingUrl;
    setUi({ phase: "running", beforeUrl, afterUrl: null, error: null });
    const controller = new AbortController();
    let jobId = null;
    runImageEditJob({
      operation,
      imageUrl: beforeUrl,
      prompt,
      settings,
      boxId: session.boxId,
      navId: session.navId,
      signal: controller.signal,
      onJobId: (id) => {
        jobId = id;
        jobIdRef.current = id;
      },
    })
      .then((url) => {
        setInlineWorkingUrl(url, jobId); // chain: next edit builds on this preview
        setUi({ phase: "done", beforeUrl, afterUrl: url, error: null });
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setUi({ phase: "error", beforeUrl, error: e?.message || "Düzenleme başarısız." });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, prompt, toolCallId]);

  const handleApply = useCallback(() => {
    const session = getInlineEditSession();
    if (!ui.afterUrl) return;
    if (typeof updateBoxImageUrl !== "function" || !session?.boxId) {
      setUi((s) => ({ ...s, error: "Editör hazır değil — sonuç uygulanamadı." }));
      return;
    }
    try {
      updateBoxImageUrl(session.boxId, ui.afterUrl);
      // Applied → close the "Onay Bekliyor" widget cards of every edit job in
      // the chain (each one is its own image_editing row).
      (session.jobIds || []).forEach(approveJob);
      clearInlineEditSession();
      setUi((s) => ({ ...s, phase: "applied" }));
    } catch (e) {
      setUi((s) => ({ ...s, error: e?.message || "Uygulanamadı." }));
    }
  }, [ui.afterUrl, updateBoxImageUrl]);

  if (ui.phase === "fallback") {
    return (
      <OpenedState prompt={prompt} settings={settings} target={null} toolCallId={toolCallId} />
    );
  }
  if (ui.phase === "error") {
    return <ErrorState message={ui.error} />;
  }
  if (ui.phase === "running") {
    return (
      <Card>
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
          <HeaderChip label={opLabel} />
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
          <SpinnerIcon
            size={15}
            className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]"
          />
          <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
            {i18n.t("EditImage.processing") || "Görsel düzenleniyor…"}
          </span>
        </div>
      </Card>
    );
  }

  // done | applied — show the result with an Apply action.
  const applied = ui.phase === "applied";
  const declined = ui.phase === "trayDeclined";
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label={opLabel} />
      </div>
      {ui.afterUrl && (
        <div className="tw-px-3 tw-py-2">
          <img
            src={ui.afterUrl}
            alt={opLabel}
            className="tw-w-full tw-rounded-lg tw-border tw-border-[var(--aidea-border)]"
          />
        </div>
      )}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-pb-3">
        <span className="tw-min-w-0 tw-truncate tw-text-[12px] tw-text-[var(--aidea-muted-fg)]">
          {applied
            ? "Görsele uygulandı."
            : declined
              ? "İş tepsisinden reddedildi."
              : ui.afterUrl
                ? "Yeni bir istek yazarak düzenlemeye devam edebilir, ya da uygulayabilirsin."
                : "Bu düzenleme tamamlandı."}
        </span>
        {!applied && !declined && ui.afterUrl && (
          <button
            type="button"
            onClick={handleApply}
            className="tw-flex-none tw-rounded-full tw-bg-[var(--ac-ink)] tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-text-[var(--aidea-on-primary)] tw-cursor-pointer hover:tw-opacity-90"
          >
            Uygula
          </button>
        )}
      </div>
    </Card>
  );
}

// Lock the inline-vs-popup decision at first mount: the inline session is
// cleared on "Uygula", but this card must keep showing its result — it must not
// flip to the popup-opening OpenedState on a later re-render.
function EditImageCard({ parsed, toolCallId }) {
  const inlineRef = useRef(null);
  if (inlineRef.current === null) inlineRef.current = isInlineEditActive();
  if (inlineRef.current) {
    return (
      <InlineEditState
        prompt={parsed.prompt || ""}
        settings={parsed.settings || {}}
        toolCallId={toolCallId}
      />
    );
  }
  return (
    <OpenedState
      prompt={parsed.prompt || ""}
      settings={parsed.settings || {}}
      target={parsed.target || null}
      toolCallId={toolCallId}
    />
  );
}

export const EditImageToolUI = makeAssistantToolUI({
  toolName: "editImage",
  render: ({ result, status, toolCallId }) => {
    if (status && status.type === "running") {
      return <RunningState />;
    }
    const parsed = parseEditImageResult(result);
    if (!parsed) {
      return <ErrorState />;
    }
    if (parsed.error) {
      return <ErrorState message={parsed.error} />;
    }
    return <EditImageCard parsed={parsed} toolCallId={toolCallId} />;
  },
});

export default EditImageToolUI;
